import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Info, Loader2, Shield } from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { groupsApi, ObjectRoleConfig, ObjectTypeConfig } from "@/services/api/groups";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

export const Route = createFileRoute("/portal/platform/object-permissions")({
  component: ObjectPermissionsConfigPage,
});

const SERVICES = ["rag", "sql_rag", "reporting", "myapp", "spaces", "lms"] as const;

const SERVICE_LABELS: Record<string, string> = {
  rag: "RAG",
  sql_rag: "SQL RAG",
  reporting: "Reporting",
  myapp: "MyApp",
  spaces: "Spaces",
  lms: "LMS",
};

interface ServiceConfig {
  roles: ObjectRoleConfig[];
  object_types: ObjectTypeConfig[];
}

function ObjectPermissionsConfigPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId;
  const [activeService, setActiveService] = useState<string>("rag");
  const [configs, setConfigs] = useState<Record<string, ServiceConfig>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadAll() {
      if (!token || !orgId) return;
      setLoading(true);
      const results: Record<string, ServiceConfig> = {};
      await Promise.all(
        SERVICES.map(async (svc) => {
          try {
            const data = await groupsApi.getObjectPermissionConfig(token, orgId, svc);
            results[svc] = { roles: data.roles, object_types: data.object_types };
          } catch {
            results[svc] = { roles: [], object_types: [] };
          }
        }),
      );
      setConfigs(results);
      setLoading(false);
    }
    loadAll();
  }, [token, orgId]);

  const _config = configs[activeService];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6" />
          Object Permission Config
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Roles and object types configured per service for object-level access
          control.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          This configuration is currently managed via seed data and applied on
          service startup. Contact a developer to make changes.
        </AlertDescription>
      </Alert>

      {loading ? (
        <div className="py-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className="rounded-xl border bg-white p-6 shadow-sm">
          <Tabs value={activeService} onValueChange={setActiveService}>
            <TabsList>
              {SERVICES.map((svc) => (
                <TabsTrigger key={svc} value={svc}>
                  {SERVICE_LABELS[svc]}
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {configs[svc]?.roles.length ?? 0}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            {SERVICES.map((svc) => (
              <TabsContent key={svc} value={svc} className="space-y-6 mt-4">
                {/* Roles */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">
                    Role Hierarchy
                    <span className="text-muted-foreground font-normal ml-1">
                      (lowest to highest privilege)
                    </span>
                  </h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">Level</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {configs[svc]?.roles.map((role) => (
                        <TableRow key={role.code}>
                          <TableCell>
                            <Badge variant="outline">{role.sort_order}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {role.code}
                          </TableCell>
                          <TableCell>{role.display_name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {role.description ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Object Types */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Object Types</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Display Name</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {configs[svc]?.object_types.map((t) => (
                        <TableRow key={t.object_type}>
                          <TableCell className="font-mono text-sm">
                            {t.object_type}
                          </TableCell>
                          <TableCell>{t.display_name}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {t.description ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}
    </div>
  );
}
